import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { FieldError, useFormContext } from 'react-hook-form';
import { get } from 'lodash';
import {
  OutlineCard,
  FormFeedback,
  Checkbox,
  TextArea
} from '@morressier/alembic';
import { FieldLabel } from 'src/components/Field';
import { RadioGroup } from 'src/components/RadioGroup';
import { FormFieldAITools } from '@publishing-engine/domain/types/article';
import { ISubmissionMetadata } from '@publishing-engine/domain/models';
import { useEffect } from 'react';
import { getValidationTypedMessage } from 'src/components/FormField/common/errorMessages';
import { toSentenceCasePreserveAcronyms } from 'src/utils/string';

export type IAITool = {
  metadata: {
    aiTools: ISubmissionMetadata['aiTools'];
  };
};

const AITools = ({ config }: { config: FormFieldAITools }) => {
  const {
    params: { validations, labels },
    caseDataFields: { main: field, dependsOn }
  } = config;

  const {
    register,
    watch,
    control,
    setValue,
    clearErrors,
    formState: { errors }
  } = useFormContext<IAITool>();

  const dependsOnField = `${field}.${dependsOn}` as const; // e.g. metadata.aiTools.exists
  const mainField = `${field}.value` as const;

  const descriptionField = `${field}.description` as const;

  const error = get(errors, mainField) as FieldError;

  const value = (watch(mainField) as string[]) || [];

  const description = (watch(descriptionField) as string) || '';

  const descriptionError = get(errors, descriptionField) as FieldError;

  const exists = watch(dependsOnField);

  useEffect(() => {
    if (!exists) {
      setValue(mainField, []);
      setValue(descriptionField, '');
      clearErrors(mainField);
      clearErrors(field);
    }
  }, [exists, setValue, clearErrors, mainField]);

  return (
    <Flex flexDirection="column" gap={8} width="100%">
      {/* === Radio Section === */}
      <OutlineCard padding={4}>
        <Flex flexDirection="column" gap={4}>
          <FieldLabel
            isRequired={validations.required}
            label={labels.main}
            subText={labels.subtitle}
            helpText={labels.helpText}
          />
          <RadioGroup
            name={dependsOnField}
            control={control}
            options={[
              {
                value: true,
                children: (
                  <>
                    <Text>{labels.options['true']?.main}</Text>
                    {/* === Dependent Checkboxes Section === */}
                    {exists && (
                      <Flex flexDirection="column" gap={4} padding={2}>
                        <VStack align="start" spacing={3}>
                          {Object.entries(labels?.dependentOptions ?? {}).map(
                            ([key, option]) => (
                              <Checkbox
                                key={key}
                                value={key}
                                ariaLabel={option.main}
                                id={`${field}-${option.main}`}
                                isChecked={value.includes(key)}
                                {...register(mainField as any, {
                                  required: validations.required
                                })}
                              >
                                {option?.main ?? key}
                              </Checkbox>
                            )
                          )}

                          {error && (
                            <FormFeedback variant="error">
                              {getValidationTypedMessage(
                                toSentenceCasePreserveAcronyms(labels.main),
                                validations.kind,
                                validations,
                                error
                              )}
                            </FormFeedback>
                          )}
                        </VStack>
                        <FieldLabel
                          label={''}
                          subText={
                            <>
                              {labels.section.explanation}
                              <Box as="span" color="red.500">
                                *
                              </Box>
                            </>
                          }
                          isRequired={false} // Removed required badge only in UI and added * in label
                        />
                        <TextArea
                          value={description}
                          placeholder={
                            'Please explain which AI tools were used'
                          }
                          {...register(descriptionField, {
                            required: validations.required
                          })}
                          aria-label="AI Tools specification"
                          isInvalid={!!descriptionError}
                          errorMessage={
                            descriptionError &&
                            getValidationTypedMessage(
                              toSentenceCasePreserveAcronyms(labels.main),
                              validations.kind,
                              validations,
                              descriptionError
                            )
                          }
                        />
                      </Flex>
                    )}
                  </>
                )
              },
              {
                value: false,
                children: labels?.options?.['false']?.main ?? 'No'
              }
            ]}
          />

          {get(errors, dependsOnField) && (
            <FormFeedback variant="error">
              Please indicate whether AI tools were used in this manuscript
            </FormFeedback>
          )}
        </Flex>
      </OutlineCard>
    </Flex>
  );
};

export default AITools;
